import React from 'react';
import { Check, X, Rabbit, Zap, Heart, Shield } from 'lucide-react';

const HEADING = { fontFamily: "'Space Grotesk', system-ui, sans-serif" } as const;

const COMPARISONS = [
  { bad: 'Outils surchargés de fonctionnalités inutiles', good: "Interface épurée, focalisée sur l'essentiel" },
  { bad: 'Configuration complexe pendant des semaines', good: 'Opérationnel en 10 minutes chrono' },
  { bad: "Courbe d'apprentissage décourageante", good: 'Prise en main intuitive immédiate' },
  { bad: 'Tarification opaque et coûteuse', good: 'Prix transparent, adapté aux équipes' },
  { bad: 'Support distant et impersonnel', good: 'Équipe réactive, vraies réponses' },
];

const VALUES = [
  { icon: Zap, title: 'Rapidité', description: 'Chaque clic compte. Optimisé pour la vitesse.' },
  { icon: Heart, title: 'Simplicité', description: "Pas de menus à rallonge. Juste l'essentiel." },
  { icon: Shield, title: 'Fiabilité', description: 'Vos données en sécurité. Toujours disponible.' },
];

export function WhyLapynoSection() {
  return (
    <section id="why" className="py-24 md:py-32 bg-[#0a0a1a] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1e1e5a] text-[#a5b4fc] text-xs font-bold uppercase tracking-widest mb-6 border border-[#4f46e5]/30">
            <Rabbit className="h-3.5 w-3.5" />
            Pourquoi Lapyno
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 tracking-tighter" style={HEADING}>
            Conçu pour les équipes{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4f46e5] to-[#a5b4fc]">
              qui veulent avancer
            </span>
          </h2>
          <p className="text-lg text-slate-400">
            Assez des outils qui ralentissent plus qu'ils n'aident. Lapyno est pensé pour le travail réel.
          </p>
        </div>

        <div className="max-w-5xl mx-auto mb-20 grid md:grid-cols-2 gap-6">
          <div className="p-8 bg-[#141432] rounded-[2rem] border border-red-900/40">
            <div className="flex items-center gap-2 mb-6">
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <X className="h-5 w-5 text-red-400" />
              </div>
              <span className="font-bold text-red-300" style={HEADING}>Les autres outils</span>
            </div>
            <ul className="space-y-4">
              {COMPARISONS.map((item) => (
                <li key={item.bad} className="flex items-start gap-3 text-slate-400">
                  <X className="h-5 w-5 mt-0.5 shrink-0 text-red-500/70" />
                  <span>{item.bad}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 bg-gradient-to-br from-[#141432] to-[#1e1e5a] rounded-[2rem] border border-[#4f46e5]/40 shadow-[0_20px_60px_-20px_rgba(79,70,229,0.4)]">
            <div className="flex items-center gap-2 mb-6">
              <div className="p-2 rounded-lg bg-[#4f46e5]/15 border border-[#4f46e5]/30">
                <Check className="h-5 w-5 text-[#a5b4fc]" />
              </div>
              <span className="font-bold text-white" style={HEADING}>Lapyno</span>
            </div>
            <ul className="space-y-4">
              {COMPARISONS.map((item) => (
                <li key={item.good} className="flex items-start gap-3 text-slate-100">
                  <Check className="h-5 w-5 mt-0.5 shrink-0 text-[#a5b4fc]" />
                  <span>{item.good}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((value) => {
            const Icon = value.icon;
            return (
              <div
                key={value.title}
                className="p-8 rounded-[2rem] bg-[#141432] border border-[#1e1e5a] text-center hover:border-[#4f46e5]/50 transition-colors"
              >
                <div className="inline-flex p-4 rounded-2xl bg-[#1e1e5a] border border-[#4f46e5]/20 mb-4">
                  <Icon className="h-7 w-7 text-[#a5b4fc]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" style={HEADING}>{value.title}</h3>
                <p className="text-slate-400">{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
